/**
 * SharePointList — operations on a single SharePoint list.
 * Obtain instances via `spClient.list(identifier)`; the client owns
 * the per-identifier cache.
 */

import { getListEndpointFragment, GUID_REGEX } from './utils.js';

/**
 * Columns that SharePoint adds to every item and that should be stripped
 * before sending an update payload, or excluded from `$select` building.
 */
export const PERMANENT_COLUMNS = ['ID', '__metadata', 'FileSystemObjectType'];

export class ListTooLargeError extends Error {
  constructor(totalCount, maxItemsCount) {
    super(
      `List contains ${totalCount} items, which exceeds the maximum allowed limit of ${maxItemsCount} items`
    );
    this.name = 'ListTooLargeError';
    this.totalCount = totalCount;
    this.maxItemsCount = maxItemsCount;
  }
}

export class SharePointList {
  /**
   * @param {object} options
   * @param {string} options.listIdentifier - SharePoint list title or GUID
   * @param {object} options.spClient - SharePoint client instance
   */
  constructor(options) {
    if (!options?.listIdentifier || !options?.spClient) {
      throw new Error(
        'SharePointList: Missing required options (listIdentifier, spClient)'
      );
    }
    this._listIdentifier = options.listIdentifier;
    this._spClient = options.spClient;
    this._listEndpointFragment = getListEndpointFragment(this._listIdentifier);
    this._fields = null;
    this._title = null;
    this._lastFieldsFetch = null;
    this._lastTitleFetch = null;
    this._entityTypeFullNamePromise = null;
  }

  get listIdentifier() {
    return this._listIdentifier;
  }

  get listEndpointFragment() {
    return this._listEndpointFragment;
  }

  get isGuid() {
    return GUID_REGEX.test(this._listIdentifier);
  }

  /**
   * Fetches and returns the list's title. Caches the result.
   * @param {boolean} [forceRefresh=false]
   * @returns {Promise<string>}
   */
  async getTitle(forceRefresh = false) {
    if (!forceRefresh && this._title) {
      return this._title;
    }

    if (!this.isGuid) {
      this._title = this._listIdentifier;
      this._lastTitleFetch = Date.now();
      return this._title;
    }

    const listInfo = await this._spClient.request(
      `web/lists/${this._listEndpointFragment}?$select=Title`,
      'GET'
    );
    const title = listInfo?.d?.Title;
    if (!title) {
      throw new Error(
        `Could not retrieve title for list "${this._listIdentifier}"`
      );
    }
    this._title = title;
    this._lastTitleFetch = Date.now();
    return this._title;
  }

  /**
   * Fetches and returns the list's fields. Caches the result.
   * @param {boolean} [forceRefresh=false]
   * @returns {Promise<Array<object>>}
   */
  async getFields(forceRefresh = false) {
    if (!forceRefresh && this._fields) {
      return this._fields;
    }

    const endpoint =
      `web/lists/${this._listEndpointFragment}/fields?` +
      `$select=InternalName,Title,TypeAsString,FieldTypeKind,Required,Id,Description,Indexed,EnforceUniqueValues,MaxLength,DefaultValue,ReadOnlyField,Hidden,FromBaseType,Choices,NumberOfLines,AllowMultipleValues,LookupField`;

    const response = await this._spClient.request(endpoint, 'GET');
    const fields = response?.d?.results || [];
    this._fields = fields;
    this._lastFieldsFetch = Date.now();
    return fields;
  }

  /**
   * Get the ListItemEntityTypeFullName for the list. Required for item creation/updates.
   * @returns {Promise<string>}
   */
  async getListItemEntityTypeFullName() {
    if (this._entityTypeFullNamePromise) {
      return this._entityTypeFullNamePromise;
    }

    const promise = (async () => {
      const response = await this._spClient.request(
        `web/lists/${this._listEndpointFragment}?$select=ListItemEntityTypeFullName`,
        'GET'
      );
      const entityTypeFullName = response?.d?.ListItemEntityTypeFullName;
      if (!entityTypeFullName) {
        throw new Error(
          `ListItemEntityTypeFullName not found for list "${this._listIdentifier}"`
        );
      }
      return entityTypeFullName;
    })();

    this._entityTypeFullNamePromise = promise;
    promise.catch(() => {
      if (this._entityTypeFullNamePromise === promise) {
        this._entityTypeFullNamePromise = null;
      }
    });
    return promise;
  }

  /**
   * Fetches general list metadata (title, entity type, etc.).
   * @returns {Promise<object>}
   */
  async getInfo() {
    const response = await this._spClient.request(
      `web/lists/${this._listEndpointFragment}`,
      'GET'
    );
    return response?.d;
  }

  /**
   * Fetches the current change token for the list.
   * @returns {Promise<string>}
   */
  async getListCurrentChangeToken() {
    const response = await this._spClient.request(
      `web/lists/${this._listEndpointFragment}?$select=CurrentChangeToken`,
      'GET'
    );
    const token = response?.d?.CurrentChangeToken?.StringValue;
    if (!token) {
      throw new Error('CurrentChangeToken not found in SharePoint response.');
    }
    return token;
  }

  /**
   * Fetches a single item by ID.
   * @param {number} itemId
   * @returns {Promise<object>}
   */
  async getItem(itemId) {
    const response = await this._spClient.request(
      `web/lists/${this._listEndpointFragment}/items(${itemId})`,
      'GET'
    );
    return response?.d;
  }

  /**
   * Creates a new item in the list.
   * @param {object} data - Item fields (no __metadata needed)
   * @returns {Promise<object>}
   */
  async createItem(data) {
    const entityTypeFullName = await this.getListItemEntityTypeFullName();
    const response = await this._spClient.request(
      `web/lists/${this._listEndpointFragment}/items`,
      'POST',
      {
        body: JSON.stringify({
          __metadata: { type: entityTypeFullName },
          ...data,
        }),
      },
      true
    );
    return response?.d;
  }

  /**
   * Updates an existing item using a MERGE operation.
   * @param {number} itemId
   * @param {object} data - Fields to update
   * @param {string} etag - ETag for optimistic concurrency
   */
  async updateItem(itemId, data, etag) {
    const entityTypeFullName = await this.getListItemEntityTypeFullName();
    await this._spClient.request(
      `web/lists/${this._listEndpointFragment}/items(${itemId})`,
      'MERGE',
      {
        body: JSON.stringify({
          __metadata: { type: entityTypeFullName },
          ...data,
        }),
        headers: { 'If-Match': etag },
      },
      true
    );
  }

  /**
   * Deletes an item from the list.
   * @param {number} itemId
   * @param {string} etag - ETag for optimistic concurrency
   */
  async deleteItem(itemId, etag) {
    await this._spClient.request(
      `web/lists/${this._listEndpointFragment}/items(${itemId})`,
      'DELETE',
      { headers: { 'If-Match': etag } },
      true
    );
  }

  /**
   * Updates an item from its full retrieved shape (as returned by
   * `getItem()` / `fetchPaginatedItems()`). Reads the ID and ETag from
   * `item.__metadata`, strips permanent columns from the payload.
   *
   * @param {object} item - Full SharePoint item (must include `__metadata`)
   * @returns {Promise<void>}
   */
  async updateItemFromObject(item) {
    if (!item?.__metadata) {
      throw new Error('updateItemFromObject: item must include __metadata');
    }
    const data = Object.fromEntries(
      Object.entries(item).filter(([key]) => !PERMANENT_COLUMNS.includes(key))
    );
    return this.updateItem(item.ID, data, item.__metadata.etag);
  }

  /**
   * Deletes an item from its full retrieved shape.
   *
   * @param {object} item - Full SharePoint item (must include `__metadata`)
   * @returns {Promise<void>}
   */
  async deleteItemFromObject(item) {
    if (!item?.__metadata) {
      throw new Error('deleteItemFromObject: item must include __metadata');
    }
    return this.deleteItem(item.ID, item.__metadata.etag);
  }

  /** Clears the cached fields data, forcing a refresh on next getFields() call. */
  clearFieldsCache() {
    this._fields = null;
    this._lastFieldsFetch = null;
  }

  /** @returns {number|null} Age in milliseconds or null if no cache */
  get fieldsCacheAge() {
    if (!this._lastFieldsFetch) return null;
    return Date.now() - this._lastFieldsFetch;
  }

  /**
   * Adds a new field to the list.
   * @param {object} field - Field definition (must include Title and FieldTypeKind)
   * @returns {Promise<object|null>} Created field, or null if it already existed
   */
  async createField(field) {
    if (!field.Title || field.FieldTypeKind === undefined) {
      throw new Error(
        'createField: Missing required field properties (Title, FieldTypeKind)'
      );
    }

    const fieldPayload = {
      __metadata: field.__metadata || { type: 'SP.Field' },
      ...field,
    };

    try {
      const response = await this._spClient.request(
        `web/lists/${this._listEndpointFragment}/fields`,
        'POST',
        { body: JSON.stringify(fieldPayload) },
        true
      );
      this.clearFieldsCache();
      return response.d;
    } catch (error) {
      if (error.message?.includes('already exists')) {
        console.warn(
          `Field '${field.Title}' already exists in list '${this._listIdentifier}'. Skipping.`
        );
        return null;
      }
      throw error;
    }
  }

  /**
   * Creates multiple fields in the list.
   * @param {Array<object>} fieldDefinitions
   * @returns {Promise<Array>}
   */
  async createFields(fieldDefinitions) {
    if (!Array.isArray(fieldDefinitions) || fieldDefinitions.length === 0) {
      throw new Error(
        'createFields: fieldDefinitions must be a non-empty array'
      );
    }

    const results = [];
    for (const fieldDef of fieldDefinitions) {
      try {
        const result = await this.createField(fieldDef);
        results.push({ name: fieldDef.name, success: true, result });
      } catch (error) {
        results.push({
          name: fieldDef.name,
          success: false,
          error: error.message,
        });
      }
    }
    return results;
  }

  /**
   * Fetches items from the list using pagination.
   * @param {object} [options]
   * @param {number} [options.batchSize=5000]
   * @param {number} [options.maxItemsCount=20000]
   * @param {string} [options.$select]
   * @param {string} [options.$expand]
   * @param {string} [options.$filter]
   * @returns {Promise<Array>}
   * @throws {ListTooLargeError}
   */
  async fetchPaginatedItems(options = {}) {
    const {
      batchSize = 5000,
      maxItemsCount = 20000,
      $select,
      $expand,
      $filter,
    } = options;

    let queryParams = '';
    if ($select) queryParams += `&$select=${$select}`;
    if ($expand) queryParams += `&$expand=${$expand}`;
    if ($filter) queryParams += `&$filter=${$filter}`;

    const items = [];
    const initialResponse = await this._spClient.request(
      `web/lists/${this._listEndpointFragment}/items?$orderby=ID&$top=${batchSize}${queryParams}`,
      'GET'
    );
    const initialItems = initialResponse?.d?.results || [];
    items.push(...initialItems);

    if (initialItems.length === batchSize && initialResponse?.d?.__next) {
      const countResponse = await this._spClient.request(
        `web/lists/${this._listEndpointFragment}/ItemCount`,
        'GET'
      );
      const totalCount = countResponse?.d?.ItemCount || 0;
      if (totalCount > maxItemsCount) {
        throw new ListTooLargeError(totalCount, maxItemsCount);
      }

      let skipCount = batchSize;
      let hasNext = true;
      while (hasNext) {
        const response = await this._spClient.request(
          `web/lists/${this._listEndpointFragment}/items?$orderby=ID&$top=${batchSize}&$skiptoken=Paged%3dTRUE%26p_ID%3d${skipCount}${queryParams}`,
          'GET'
        );
        const batchItems = response?.d?.results || [];
        items.push(...batchItems);
        hasNext = batchItems.length === batchSize && response?.d?.__next;
        skipCount += batchSize;
      }
    }

    return items;
  }
}
